import { Layer } from "@common/constants";
import { mapWeather, type SkyName } from "@common/utils/mapWeather";
import { Vec, type Vector } from "@common/utils/vector";
import { Container, Sprite, Texture } from "pixi.js";
import { Game } from "../game";
import { PIXI_SCALE } from "../utils/constants";
import { CameraManager } from "./cameraManager";
import { MapManager } from "./mapManager";
import { NIGHT_ZINDEX } from "./nightLightLogic";
import { SoundManager } from "./soundManager";

/*
    The night made creepier (0.98.4, owner decision, 31 October 2026: "Night mode, lower the rightness to 87% ... and
    make it creepier"): with the darker, colder dark (nightLightLogic.ts) comes a mist that creeps over the ground,
    pale wisps drifting with the wind over the dark, and the night's sounds, an owl, a far howl, a crow starting up, a
    creak, now and then from somewhere out in the dark round you (tools/sfx/sky_sounds.py).
*/

/** the wisps: how many, their size (units), their drift (a share of the wind), their palest and their brightest */
const WISPS = 16;
const WISP_LENGTH = 34;
const WISP_WIDTH = 11;
const WISP_ALPHA = Object.freeze({ min: 0.05, max: 0.13 });
/** the night's sounds: how long between them (ms), from how far (units) */
const SOUND_EVERY = Object.freeze({ min: 22_000, max: 48_000 });
const SOUND_REACH = Object.freeze({ min: 55, max: 95 });
const NIGHT_SOUNDS = ["night_owl", "night_howl", "night_crow", "night_creak"];

interface Wisp {
    readonly sprite: Sprite
    /** where it is, as a share of the screen (it comes round the other side as it drifts off) */
    u: number
    v: number
    readonly seed: number
}

let wispTexture: Texture | undefined;
/** a wisp's picture, made once: a soft, long, pale smear */
const getWispTexture = (): Texture => {
    if (wispTexture) return wispTexture;
    const width = 256, height = 96;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.save();
        ctx.translate(width / 2, height / 2);
        ctx.scale(width / 2, height / 2);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
        g.addColorStop(0, "rgba(255,255,255,0.9)");
        g.addColorStop(0.45, "rgba(255,255,255,0.45)");
        g.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, 1, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
    return wispTexture = Texture.from(canvas);
};

class NightCreepClass {
    private _night = false;
    private _layer?: Container;
    private _wisps: Wisp[] = [];
    private _wind = Vec(1, 0);
    private _windSeed = -1;
    private _nextSound = 0;

    get night(): boolean { return this._night; }

    begin(sky: SkyName): void {
        this.reset();
        this._night = sky === "night";
        this._nextSound = Date.now() + SOUND_EVERY.min * 0.6;
    }

    private _container(): Container {
        if (this._layer) return this._layer;
        const layer = new Container();
        // (over the night's dark: pale against it)
        layer.zIndex = NIGHT_ZINDEX + 0.5;
        layer.eventMode = "none";
        CameraManager.addObject(layer);
        this._layer = layer;
        return layer;
    }

    private _view(): { readonly min: Vector, readonly max: Vector } {
        const scale = CameraManager.container.scale.x || 1;
        const halfW = CameraManager.width / scale / 2 / PIXI_SCALE + WISP_LENGTH;
        const halfH = CameraManager.height / scale / 2 / PIXI_SCALE + WISP_LENGTH;
        const center = Vec.scale(CameraManager.position, 1 / PIXI_SCALE);
        return { min: Vec(center.x - halfW, center.y - halfH), max: Vec(center.x + halfW, center.y + halfH) };
    }

    update(delta: number): void {
        if (!this._night || !Game.gameStarted) return;
        if (MapManager.seed !== this._windSeed) {
            this._windSeed = MapManager.seed;
            const weather = mapWeather(MapManager.seed, "night");
            this._wind = Vec(Math.cos(weather.windAngle) * weather.windSpeed, Math.sin(weather.windAngle) * weather.windSpeed);
        }
        const now = Date.now();
        const t = now / 1000;
        if (!this._wisps.length) {
            const layer = this._container();
            for (let i = 0; i < WISPS; i++) {
                const sprite = new Sprite(getWispTexture());
                sprite.anchor.set(0.5);
                sprite.tint = 0x9fb2c6;
                layer.addChild(sprite);
                this._wisps.push({ sprite, u: Math.random(), v: Math.random(), seed: Math.random() * 100 });
            }
        }
        const view = this._view();
        const w = view.max.x - view.min.x, h = view.max.y - view.min.y;
        const windAngle = Math.atan2(this._wind.y, this._wind.x);
        for (const wisp of this._wisps) {
            // (a slow drift with the wind, and a sway of its own; off one side, back on the other)
            const sway = Math.sin(t * 0.21 + wisp.seed) * 0.4;
            wisp.u = ((wisp.u + (this._wind.x * 0.55 + sway * 0.2) * delta / 1000 / w) % 1 + 1) % 1;
            wisp.v = ((wisp.v + (this._wind.y * 0.55 + Math.cos(t * 0.17 + wisp.seed) * 0.2) * delta / 1000 / h) % 1 + 1) % 1;
            const x = view.min.x + wisp.u * w, y = view.min.y + wisp.v * h;
            const sprite = wisp.sprite;
            sprite.position.set(x * PIXI_SCALE, y * PIXI_SCALE);
            sprite.rotation = windAngle + Math.sin(t * 0.3 + wisp.seed) * 0.25;
            const breathe = 1 + 0.15 * Math.sin(t * 0.5 + wisp.seed * 2);
            sprite.scale.set((WISP_LENGTH * PIXI_SCALE * breathe) / 256, (WISP_WIDTH * PIXI_SCALE) / 96);
            // (fading in and out of being, each in its own time, never all at once)
            const being = 0.5 + 0.5 * Math.sin(t * 0.13 + wisp.seed * 3);
            sprite.alpha = WISP_ALPHA.min + (WISP_ALPHA.max - WISP_ALPHA.min) * being;
        }
        // the night's sounds, from somewhere out in the dark
        const me = Game.activePlayer;
        if (me && now >= this._nextSound) {
            this._nextSound = now + SOUND_EVERY.min + Math.random() * (SOUND_EVERY.max - SOUND_EVERY.min);
            const name = NIGHT_SOUNDS[Math.floor(Math.random() * NIGHT_SOUNDS.length)];
            const reach = SOUND_REACH.min + Math.random() * (SOUND_REACH.max - SOUND_REACH.min);
            const position = Vec.add(me.position, Vec.fromPolar(Math.random() * Math.PI * 2, reach));
            SoundManager.play(name, { position, falloff: 0.25, maxRange: 240, layer: Layer.Ground, variation: { pitch: 0.06 }, air: true });
        }
    }

    reset(): void {
        for (const wisp of this._wisps) wisp.sprite.destroy();
        this._wisps = [];
        this._layer?.destroy({ children: true });
        this._layer = undefined;
        this._night = false;
        this._windSeed = -1;
    }
}

export const NightCreep = new NightCreepClass();
